import React, { useState } from 'react';
import { Button, Popover, Box, Typography, Checkbox, FormControlLabel, FormGroup } from '@material-ui/core';
import { Visibility, VisibilityOff, ArrowDropDown } from '@material-ui/icons';
import { useCampfireTheme } from '../../../../../../../../theme/useCampfireTheme';

export type Program = {
  programId: string;
  name: string;
  dateSuspended: Date | null;
};

interface FieldVisibilityControlProps {
  hiddenForProgramIds: string[] | null | undefined;
  onChange: (programIds: string[]) => void;
  programs: Program[];
}

export const FieldVisibilityControl: React.FC<FieldVisibilityControlProps> = ({
  hiddenForProgramIds,
  onChange,
  programs,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { theme } = useCampfireTheme();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggleProgram = (programId: string) => {
    const currentHidden = hiddenForProgramIds || [];
    const isHidden = currentHidden.includes(programId);

    if (isHidden) {
      onChange(currentHidden.filter((id) => id !== programId));
    } else {
      onChange([...currentHidden, programId]);
    }
  };

  const open = Boolean(anchorEl);
  const hasHiddenPrograms = (hiddenForProgramIds || []).length > 0;

  return (
    <>
      <Button
        size='small'
        variant='outlined'
        onClick={handleClick}
        startIcon={hasHiddenPrograms ? <VisibilityOff /> : <Visibility />}
        endIcon={<ArrowDropDown />}
        style={{
          border: '1px solid #9e9e9e',
          boxSizing: 'border-box',
          borderRadius: '4px',
          padding: '5px 8px',
          color: theme.color.grey.neutral300,
          textTransform: 'none',
          minWidth: 'unset',
        }}
        sx={{
          '& .MuiButton-startIcon': {
            marginRight: '-4px',
            marginLeft: 0,
          },
          '& .MuiButton-endIcon': {
            marginLeft: '-4px',
            marginRight: 0,
          },
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box p={2} style={{ minWidth: 250, maxWidth: 350 }}>
          <Typography variant='subtitle2' gutterBottom>
            Hide field for programs
          </Typography>
          <Typography variant='caption' color='textSecondary' style={{ marginBottom: 8, display: 'block' }}>
            Select programs where this field should be hidden
          </Typography>

          {programs.length === 0 ? (
            <Typography variant='body2' color='textSecondary' style={{ fontStyle: 'italic', marginTop: 8 }}>
              No programs available
            </Typography>
          ) : (
            <FormGroup>
              {programs.map((program) => (
                <FormControlLabel
                  key={program.programId}
                  control={
                    <Checkbox
                      checked={(hiddenForProgramIds || []).includes(program.programId)}
                      onChange={() => handleToggleProgram(program.programId)}
                      color='primary'
                      size='small'
                    />
                  }
                  label={
                    <Typography variant='body2'>
                      {program.name}
                      {program.dateSuspended && (
                        <span style={{ color: theme.palette.text.disabled }}> (Suspended)</span>
                      )}
                    </Typography>
                  }
                />
              ))}
            </FormGroup>
          )}
        </Box>
      </Popover>
    </>
  );
};
